import {
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { Subscription, filter } from "rxjs";
import {
  ProductTourService,
  ProductTourStep,
} from "../../services/product-tour/product-tour.service";

@Component({
  selector: "app-product-tour",
  templateUrl: "./product-tour.component.html",
  styleUrls: ["./product-tour.component.scss"],
})
export class ProductTourComponent implements OnInit, OnDestroy {
  @Input() inlineLauncher = false;
  readonly defaultCharacterPositionClass = "tour-popover--character-left";
  readonly dialogFormTourCharacterAsset = "assets/images/DetailMenu.png";
  readonly dropdownTourCharacterAsset = "assets/images/SmallMenu.png";
  readonly redirectionTourCharacterAsset = "assets/images/SmallMenu2.png";
  readonly dialogOpenButtonTourCharacterAsset = "assets/images/hello.png";
  readonly completeTourCharacterAsset = "assets/images/tour-complete-thumb.png";
  readonly profileMenuTourCharacterAsset = "assets/images/Addcompany.png";
  readonly characterPositionByTarget: Record<string, string> = {
    "header-profile-menu": "tour-popover--character-right",
    "header-organization-management": "tour-popover--character-right",
    "nav-tasks": "tour-popover--character-right",
    "nav-projects": "tour-popover--character-right",
    "open-company": "tour-popover--character-right",
    "company-members-view-all": "tour-popover--character-left",
    "company-members-view-all-action": "tour-popover--character-left",
    "add-company": "tour-popover--character-left",
    "add-member": "tour-popover--character-left",
    "add-task": "tour-popover--character-left",
    "add-project": "tour-popover--character-left",
    "add-company-details": "tour-popover--character-right",
    "add-member-details": "tour-popover--character-right",
    "add-task-details": "tour-popover--character-right",
    "add-project-details": "tour-popover--character-right",
  };
  readonly targetStyleClassByTarget: Record<string, string> = {
    "header-profile-menu": "tour-target--profile-menu",
    "header-organization-management": "tour-target--org-menu",
    "open-company": "tour-target--open-company",
    "nav-tasks": "tour-target--nav-tasks",
    "nav-projects": "tour-target--nav-projects",
    "add-project-details": "tour-target--project-details",
    "add-company-details": "tour-target--company-details",
    "add-member-details": "tour-target--member-details",
    "add-task-details": "tour-target--task-details",
    "company-members-view-all": "tour-target--members-open",
    "company-members-view-all-action": "tour-target--members-open",
  };
  readonly dialogFormTargets = new Set([
    "add-company-details",
    "add-member-details",
    "add-task-details",
    "add-project-details",
  ]);
  readonly dialogOpenButtonTargets = new Set([
    "add-company",
    "add-member",
    "add-task",
    "add-task-top",
    "add-project",
  ]);
  readonly dropdownOrRedirectionTargets = new Set([
    "header-profile-menu",
    "header-organization-management",
    "nav-tasks",
    "nav-projects",
    "open-company",
    "company-members-view-all",
    "company-members-view-all-action",
  ]);
  activeStep: ProductTourStep | null = null;
  isRunning = false;
  targetRect: DOMRect | null = null;
  cardStyle: { [key: string]: string } = {};
  popoverPlacement = "tour-popover--bottom";
  highlightClass = "";
  showHighlight = true;
  isPositionReady = false;
  activeTourTarget = "";
  shouldLiftForAttendance = false;
  private subscriptions = new Subscription();
  private domObserver: MutationObserver | null = null;
  private attendanceWidgetObserver: MutationObserver | null = null;
  private autoAdvanceCleanup: (() => void) | null = null;
  private cancelRecoveryCleanup: (() => void) | null = null;
  private outsideClickGuardCleanup: (() => void) | null = null;
  private isAutoAdvancing = false;
  private memberAutoProgressTimer: number | null = null;
  private hasSeenMemberDetailsStep = false;
  private hasTriggeredMemberAutoProgress = false;
  private hasTriggeredCompanyAutoProgress = false;
  private hasSeenTaskDetailsStep = false;
  private hasTriggeredTaskAutoProgress = false;
  private hasSeenProjectDetailsStep = false;
  private hasTriggeredProjectAutoProgress = false;
  private refreshTimer: number | null = null;
  private isRecoveringCompanyEntryStep = false;
  private isRecoveringMemberEntryStep = false;
  private isRecoveringTaskEntryStep = false;
  private isRecoveringProjectEntryStep = false;

  constructor(
    public productTourService: ProductTourService,
    private router: Router,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.watchAttendanceWidget();
    this.updateAttendanceWidgetState();
    this.subscriptions.add(
      this.productTourService.activeStep$.subscribe((step) => {
        this.activeStep = step;
        this.isPositionReady = false;
        this.targetRect = null;
        this.hasTriggeredCompanyAutoProgress = false;
        if (step?.id !== "member") {
          this.hasSeenMemberDetailsStep = false;
          this.hasTriggeredMemberAutoProgress = false;
        }
        if (step?.id !== "task") {
          this.hasSeenTaskDetailsStep = false;
          this.hasTriggeredTaskAutoProgress = false;
        }
        if (step?.id !== "project") {
          this.hasSeenProjectDetailsStep = false;
          this.hasTriggeredProjectAutoProgress = false;
        }
        this.setupOutsideClickGuard();
        this.watchDomChanges();
        this.refreshTarget();
      })
    );
    this.subscriptions.add(
      this.productTourService.isRunning$.subscribe((isRunning) => {
        this.isRunning = isRunning;
      })
    );
    this.subscriptions.add(
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe(async () => {
          await this.productTourService.handleRouteChange();
          this.updateAttendanceWidgetState();
          this.refreshTarget();
        })
    );

    window.setTimeout(() => {
      void this.productTourService.autoStartTourIfIncomplete();
    }, 0);
  }

  ngOnDestroy(): void {
    this.domObserver?.disconnect();
    this.attendanceWidgetObserver?.disconnect();
    this.autoAdvanceCleanup?.();
    this.cancelRecoveryCleanup?.();
    this.outsideClickGuardCleanup?.();
    if (this.memberAutoProgressTimer) {
      window.clearTimeout(this.memberAutoProgressTimer);
      this.memberAutoProgressTimer = null;
    }
    if (this.refreshTimer) {
      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.subscriptions.unsubscribe();
  }

  @HostListener("window:resize")
  @HostListener("window:scroll")
  refreshTarget() {
    this.updateAttendanceWidgetState();
    this.scheduleRefreshTarget();
  }

  private watchAttendanceWidget() {
    this.attendanceWidgetObserver?.disconnect();
    this.attendanceWidgetObserver = new MutationObserver(() => {
      this.updateAttendanceWidgetState();
    });
    this.attendanceWidgetObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style"],
    });
  }

  private updateAttendanceWidgetState() {
    const shouldLift = this.isAttendanceWidgetVisible();
    if (this.shouldLiftForAttendance === shouldLift) {
      return;
    }

    this.ngZone.run(() => {
      this.shouldLiftForAttendance = shouldLift;
      this.cdr.detectChanges();
    });
  }

  private isAttendanceWidgetVisible(): boolean {
    if (!this.router.url.startsWith("/dashboard")) {
      return false;
    }

    const attendanceWidget = document.querySelector(".check-in") as HTMLElement | null;
    if (!attendanceWidget) {
      return false;
    }

    const computedStyle = window.getComputedStyle(attendanceWidget);
    const rect = attendanceWidget.getBoundingClientRect();

    return (
      computedStyle.display !== "none" &&
      computedStyle.visibility !== "hidden" &&
      computedStyle.opacity !== "0" &&
      rect.width > 0 &&
      rect.height > 0
    );
  }

  private scheduleRefreshTarget(delay = 120) {
    if (this.refreshTimer) {
      window.clearTimeout(this.refreshTimer);
    }

    this.refreshTimer = window.setTimeout(() => {
      if (!this.activeStep?.selector) {
        this.targetRect = null;
        this.popoverPlacement = "tour-popover--center";
        this.cardStyle = this.getCenteredCardStyle();
        this.autoAdvanceCleanup?.();
        this.autoAdvanceCleanup = null;
        this.isPositionReady = true;
        return;
      }

      const element = this.getTargetElement(this.activeStep.selector);
      if (!element) {
        if (
          this.activeStep?.selector === "[data-tour='add-company-details']" &&
          !this.isRecoveringCompanyEntryStep
        ) {
          console.log("[ProductTour] missing add-company-details target, reopening company entry step", {
            activeStep: this.activeStep,
            route: this.router.url,
          });
          this.isRecoveringCompanyEntryStep = true;
          window.setTimeout(async () => {
            try {
              await this.productTourService.reopenCompanyEntryStep();
            } finally {
              this.isRecoveringCompanyEntryStep = false;
            }
          }, 0);
        }
        if (
          this.activeStep?.selector?.includes(
            "[data-tour='add-member-details']"
          ) &&
          !this.isRecoveringMemberEntryStep
        ) {
          console.log("[ProductTour] missing add-member-details target, reopening member entry step", {
            activeStep: this.activeStep,
            route: this.router.url,
          });
          this.isRecoveringMemberEntryStep = true;
          window.setTimeout(async () => {
            try {
              await this.productTourService.reopenMemberEntryStep();
            } finally {
              this.isRecoveringMemberEntryStep = false;
            }
          }, 0);
        }
        if (
          this.activeStep?.selector?.includes("[data-tour='add-task-details']") &&
          !this.isRecoveringTaskEntryStep
        ) {
          console.log("[ProductTour] missing add-task-details target, reopening task entry step", {
            activeStep: this.activeStep,
            route: this.router.url,
          });
          this.isRecoveringTaskEntryStep = true;
          window.setTimeout(async () => {
            try {
              await this.productTourService.reopenTaskEntryStep();
            } finally {
              this.isRecoveringTaskEntryStep = false;
            }
          }, 0);
        }
        if (
          this.activeStep?.selector?.includes("[data-tour='add-project-details']") &&
          !this.isRecoveringProjectEntryStep
        ) {
          console.log("[ProductTour] missing add-project-details target, reopening project entry step", {
            activeStep: this.activeStep,
            route: this.router.url,
          });
          this.isRecoveringProjectEntryStep = true;
          window.setTimeout(async () => {
            try {
              await this.productTourService.reopenProjectEntryStep();
            } finally {
              this.isRecoveringProjectEntryStep = false;
            }
          }, 0);
        }
        this.targetRect = null;
        this.highlightClass = "";
        this.showHighlight = true;
        this.autoAdvanceCleanup?.();
        this.autoAdvanceCleanup = null;
        this.cancelRecoveryCleanup?.();
        this.cancelRecoveryCleanup = null;
        return;
      }

      this.isRecoveringCompanyEntryStep = false;
      this.isRecoveringMemberEntryStep = false;
      this.isRecoveringTaskEntryStep = false;
      this.isRecoveringProjectEntryStep = false;

      if (!this.isElementMostlyInView(element as HTMLElement)) {
        element.scrollIntoView({ block: "center", inline: "center", behavior: "auto" });
      }
      this.positionTourAroundElement(element);
    }, delay);
  }

  getHighlightStyle() {
    if (!this.targetRect) {
      return {};
    }

    return {
      top: `${this.targetRect.top - 8}px`,
      left: `${this.targetRect.left - 8}px`,
      width: `${this.targetRect.width + 16}px`,
      height: `${this.targetRect.height + 16}px`,
    };
  }

  async startTour() {
    await this.productTourService.startTour();
  }

  async continueTour() {
    if (this.activeStep?.id === "complete") {
      this.productTourService.stopTour();
      return;
    }
    await this.productTourService.continueTour();
  }

  async stopTour() {
    await this.productTourService.disableTourPermanently();
  }

  private getCardStyle(rect: DOMRect): { [key: string]: string } {
    if (this.activeTourTarget === "add-project-details") {
      const cardWidth = Math.min(420, window.innerWidth - 32);
      const cardHeight = 244;
      const gap = 34;
      const left = Math.min(
        Math.max(16, rect.right + gap),
        window.innerWidth - cardWidth - 16
      );
      const top = Math.min(
        Math.max(16, rect.top + rect.height / 2 - cardHeight / 2),
        window.innerHeight - cardHeight - 16
      );
      const arrowTop = Math.min(
        Math.max(18, rect.top + rect.height / 2 - top - 7),
        cardHeight - 32
      );

      this.popoverPlacement = "tour-popover--right";
      return {
        top: `${top}px`,
        left: `${left}px`,
        width: `${cardWidth}px`,
        "--tour-arrow-top": `${arrowTop}px`,
      };
    }

    if (this.activeTourTarget === "add-task-details") {
      const cardWidth = Math.min(420, window.innerWidth - 32);
      const commentPanel = document.querySelector(
        ".rt-task-chat, .comment-section, .task-comment, [data-tour='add-task-details']"
      ) as HTMLElement | null;

      const commentTab = Array.from(
        document.querySelectorAll(".mat-tab-label, .mat-mdc-tab, [role='tab']")
      ).find((tab) => {
        const el = tab as HTMLElement;
        const text = (el.textContent || "").toLowerCase().trim();
        if (!text.includes("comment")) {
          return false;
        }
        const selected = el.getAttribute("aria-selected") === "true";
        const activeClass =
          el.classList.contains("mat-tab-label-active") ||
          el.classList.contains("mdc-tab--active");
        return selected || activeClass || this.isVisibleElement(el);
      }) as HTMLElement | undefined;

      const baseLeft = commentPanel ? commentPanel.getBoundingClientRect().left : rect.left;
      const baseTop = commentPanel ? commentPanel.getBoundingClientRect().top : rect.top;
      const commentTabRect = commentTab?.getBoundingClientRect();
      const anchorLeft = commentTabRect ? commentTabRect.left - 64 : baseLeft - 40;
      const topAnchor = commentTabRect ? commentTabRect.bottom + 10 : baseTop + 48;

      this.popoverPlacement = "tour-popover--right";
      return {
        top: `${Math.max(16, topAnchor)}px`,
        left: `${Math.max(16, anchorLeft)}px`,
        width: `${cardWidth}px`,
        "--tour-arrow-top": "34px",
      };
    }

    const showCharacterLayout = this.shouldShowCharacterLayout();
    const preferredWidth =
      this.activeTourTarget === "add-task-details"
        ? 470
        : this.activeTourTarget === "add-project"
          ? 540
        : showCharacterLayout
          ? 420
          : 360;
    const cardWidth = Math.min(preferredWidth, window.innerWidth - 32);
    const cardHeight = showCharacterLayout ? 220 : 150;
    const gap = 18;
    const hasSpaceRight = rect.right + cardWidth + gap + 16 < window.innerWidth;
    const hasSpaceLeft = rect.left - cardWidth - gap - 16 > 0;
    const preferSidePlacement =
      this.activeTourTarget === "add-company-details" ||
      this.activeTourTarget === "add-member-details" ||
      this.activeTourTarget === "add-task-details" ||
      this.activeTourTarget === "add-project-details";

    if (preferSidePlacement || hasSpaceRight || hasSpaceLeft) {
      const left = hasSpaceRight
        ? rect.right + gap
        : hasSpaceLeft
          ? rect.left - cardWidth - gap
          : Math.min(
              Math.max(16, rect.left + rect.width + gap),
              window.innerWidth - cardWidth - 16
            );
      const top = Math.min(
        Math.max(16, rect.top + rect.height / 2 - cardHeight / 2),
        window.innerHeight - cardHeight - 16
      );
      const adjustedTop =
        this.activeTourTarget === "add-task-details"
          ? Math.min(window.innerHeight - cardHeight - 12, top + 240)
          : top;
      const arrowTop = Math.min(
        Math.max(18, rect.top + rect.height / 2 - top - 7),
        cardHeight - 32
      );

      this.popoverPlacement = hasSpaceRight
        ? "tour-popover--right"
        : hasSpaceLeft
          ? "tour-popover--left"
          : "tour-popover--right";

      const adjustedLeft =
        this.activeTourTarget === "add-task-details"
          ? Math.max(16, left - 180)
          : left;

      return {
        top: `${adjustedTop}px`,
        left: `${adjustedLeft}px`,
        width: `${cardWidth}px`,
        "--tour-arrow-top": `${arrowTop}px`,
      };
    }

    const left = Math.min(Math.max(16, rect.left), window.innerWidth - cardWidth - 16);
    const hasSpaceBelow = rect.bottom + cardHeight + gap < window.innerHeight;
    this.popoverPlacement = hasSpaceBelow ? "tour-popover--bottom" : "tour-popover--top";
    const top = hasSpaceBelow
      ? rect.bottom + gap
      : Math.max(16, rect.top - cardHeight - gap);
    const arrowLeft = Math.min(
      Math.max(18, rect.left + rect.width / 2 - left - 7),
      cardWidth - 32
    );

    return {
      top: `${top}px`,
      left: `${left}px`,
      width: `${cardWidth}px`,
      "--tour-arrow-left": `${arrowLeft}px`,
    };
  }

  private positionTourAroundElement(element: Element) {
    this.targetRect = element.getBoundingClientRect();
    this.activeTourTarget = element.getAttribute("data-tour") || "";
    if (this.activeTourTarget === "add-member-details") {
      this.hasSeenMemberDetailsStep = true;
      this.hasTriggeredMemberAutoProgress = false;
    }
    if (this.activeTourTarget === "add-task-details") {
      this.hasSeenTaskDetailsStep = true;
      this.hasTriggeredTaskAutoProgress = false;
    }
    if (this.activeTourTarget === "add-project-details") {
      this.hasSeenProjectDetailsStep = true;
      this.hasTriggeredProjectAutoProgress = false;
    }
    this.showHighlight =
      element.getAttribute("data-tour") !== "add-company-details" &&
      element.getAttribute("data-tour") !== "add-member-details" &&
      element.getAttribute("data-tour") !== "add-task-details" &&
      element.getAttribute("data-tour") !== "add-project-details";
    this.highlightClass =
      element.getAttribute("data-tour") === "add-company-details" ||
      element.getAttribute("data-tour") === "add-member-details" ||
      element.getAttribute("data-tour") === "add-task-details" ||
      element.getAttribute("data-tour") === "add-project-details"
        ? "tour-highlight--clear"
        : "";
    this.bindAutoAdvanceOnTargetClick(element);
    this.bindCompanyDetailsCancelRecovery(element);
    this.cardStyle = this.getCardStyle(this.targetRect);
    this.isPositionReady = true;
  }

  shouldShowCharacterLayout(): boolean {
    if (this.activeStep?.id === "complete") {
      return false;
    }

    return (
      this.dialogFormTargets.has(this.activeTourTarget) ||
      this.dialogOpenButtonTargets.has(this.activeTourTarget) ||
      this.dropdownOrRedirectionTargets.has(this.activeTourTarget)
    );
  }

  getTourCharacterAsset(): string {
    if (this.activeStep?.id === "complete") {
      return this.completeTourCharacterAsset;
    }

    if (this.activeTourTarget === "add-project") {
      return this.dialogOpenButtonTourCharacterAsset;
    }

    if (this.activeTourTarget === "add-member-details") {
      return this.dropdownTourCharacterAsset;
    }

    if (this.activeTourTarget === "header-profile-menu") {
      return this.profileMenuTourCharacterAsset;
    }

    if (this.dialogOpenButtonTargets.has(this.activeTourTarget)) {
      return this.dialogOpenButtonTourCharacterAsset;
    }

    if (this.dialogFormTargets.has(this.activeTourTarget)) {
      return this.dialogFormTourCharacterAsset;
    }

    if (this.dropdownOrRedirectionTargets.has(this.activeTourTarget)) {
      return this.activeTourTarget === "header-organization-management"
        ? this.dropdownTourCharacterAsset
        : this.redirectionTourCharacterAsset;
    }

    return this.dialogFormTourCharacterAsset;
  }

  getCharacterPositionClass(): string {
    if (!this.shouldShowCharacterLayout()) {
      return "";
    }

    return (
      this.characterPositionByTarget[this.activeTourTarget] ||
      this.defaultCharacterPositionClass
    );
  }

  getTargetStyleClass(): string {
    return this.targetStyleClassByTarget[this.activeTourTarget] || "";
  }

  private bindAutoAdvanceOnTargetClick(element: Element) {
    this.autoAdvanceCleanup?.();
    this.autoAdvanceCleanup = null;

    if (!this.activeStep?.autoAdvanceOnTargetClick) {
      return;
    }

    const onClick = (event: Event) => {
      if (this.activeStep?.navigateTo) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (this.isAutoAdvancing) {
        return;
      }
      this.isAutoAdvancing = true;
      window.setTimeout(async () => {
        try {
          await this.continueTour();
        } finally {
          this.isAutoAdvancing = false;
        }
      }, 0);
    };

    element.addEventListener("click", onClick, { once: true });
    this.autoAdvanceCleanup = () => {
      element.removeEventListener("click", onClick);
    };
  }

  private bindCompanyDetailsCancelRecovery(element: Element) {
    this.cancelRecoveryCleanup?.();
    this.cancelRecoveryCleanup = null;

    if (
      this.activeStep?.selector !== "[data-tour='add-company-details']" &&
      !this.activeStep?.selector?.includes("[data-tour='add-member-details']") &&
      !this.activeStep?.selector?.includes("[data-tour='add-task-details']") &&
      !this.activeStep?.selector?.includes("[data-tour='add-project-details']")
    ) {
      return;
    }

    const cancelButton = element.querySelector(".button-cancel");
    if (!cancelButton) {
      return;
    }

    const onClick = () => {
      if (
        this.activeStep?.selector === "[data-tour='add-company-details']" &&
        this.isRecoveringCompanyEntryStep
      ) {
        return;
      }

      if (
        this.activeStep?.selector?.includes(
          "[data-tour='add-member-details']"
        ) &&
        this.isRecoveringMemberEntryStep
      ) {
        return;
      }
      if (
        this.activeStep?.selector?.includes("[data-tour='add-task-details']") &&
        this.isRecoveringTaskEntryStep
      ) {
        return;
      }
      if (
        this.activeStep?.selector?.includes("[data-tour='add-project-details']") &&
        this.isRecoveringProjectEntryStep
      ) {
        return;
      }

      window.setTimeout(async () => {
        try {
          if (this.activeStep?.selector === "[data-tour='add-company-details']") {
            this.isRecoveringCompanyEntryStep = true;
            await this.productTourService.reopenCompanyEntryStep();
            return;
          }

          this.isRecoveringMemberEntryStep = true;
          if (this.activeStep?.selector?.includes("[data-tour='add-member-details']")) {
            await this.productTourService.reopenMemberEntryStep();
            return;
          }

          if (this.activeStep?.selector?.includes("[data-tour='add-task-details']")) {
            this.isRecoveringTaskEntryStep = true;
            await this.productTourService.reopenTaskEntryStep();
            return;
          }

          this.isRecoveringProjectEntryStep = true;
          await this.productTourService.reopenProjectEntryStep();
        } finally {
          this.isRecoveringCompanyEntryStep = false;
          this.isRecoveringMemberEntryStep = false;
          this.isRecoveringTaskEntryStep = false;
          this.isRecoveringProjectEntryStep = false;
        }
      }, 0);
    };

    cancelButton.addEventListener("click", onClick, { once: true });
    this.cancelRecoveryCleanup = () => {
      cancelButton.removeEventListener("click", onClick);
    };
  }

  private getCenteredCardStyle(): { [key: string]: string } {
    const cardWidth =
      this.activeStep?.id === "complete"
        ? Math.min(640, window.innerWidth - 32)
        : Math.min(360, window.innerWidth - 32);
    return {
      top: "50%",
      left: "50%",
      width: `${cardWidth}px`,
      transform: "translate(-50%, -50%)",
    };
  }

  private getTargetElement(selector: string): Element | null {
    const dialogPrioritySelector = selector.includes("[data-tour='add-company-details']")
      ? "[data-tour='add-company-details']"
      : selector.includes("[data-tour='add-member-details']")
        ? "[data-tour='add-member-details']"
        : selector.includes("[data-tour='add-task-details']")
          ? "[data-tour='add-task-details']"
          : selector.includes("[data-tour='add-project-details']")
            ? "[data-tour='add-project-details']"
            : "";

    if (dialogPrioritySelector) {
      const priorityElements = Array.from(document.querySelectorAll(dialogPrioritySelector)).filter(
        (priorityElement) =>
          this.isVisibleElement(priorityElement) &&
          this.isValidTourMatch(dialogPrioritySelector, priorityElement)
      );

      if (priorityElements.length) {
        return priorityElements.sort((firstElement, secondElement) => {
          const firstRect = (firstElement as HTMLElement).getBoundingClientRect();
          const secondRect = (secondElement as HTMLElement).getBoundingClientRect();
          const firstArea = firstRect.width * firstRect.height;
          const secondArea = secondRect.width * secondRect.height;

          return secondArea - firstArea;
        })[0];
      }
    }

    const selectors = selector
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item);

    for (const item of selectors) {
      const elements = Array.from(document.querySelectorAll(item));
      for (const element of elements) {
        if (this.isVisibleElement(element) && this.isValidTourMatch(item, element)) {
          return element;
        }
      }
    }

    return null;
  }

  private isVisibleElement(element: Element): boolean {
    const htmlElement = element as HTMLElement;
    const rect = htmlElement.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return false;
    }

    const style = window.getComputedStyle(htmlElement);
    if (
      style.display === "none" ||
      style.visibility === "hidden" ||
      style.opacity === "0"
    ) {
      return false;
    }

    return true;
  }

  private isValidTourMatch(selector: string, element: Element): boolean {
    if (selector.includes("add-task-top")) {
      const htmlElement = element as HTMLElement;
      const plusIcon = htmlElement.querySelector("img[src*='whiteplusicon']");
      const hasPlusIcon = !!plusIcon && this.isVisibleElement(plusIcon);
      const buttonText = (htmlElement.textContent || "").toLowerCase();
      const hasAddTaskText =
        buttonText.includes("add task") && !buttonText.includes("activity");
      const closeStateIcon = htmlElement.querySelector(
        ".fa-times, .fa-times-circle, .close, [aria-label*='close']"
      );
      const isCloseStateVisible =
        !!closeStateIcon && this.isVisibleElement(closeStateIcon);

      return !isCloseStateVisible && (hasPlusIcon || hasAddTaskText);
    }

    return true;
  }

  private watchDomChanges() {
    this.domObserver?.disconnect();
    this.domObserver = null;

    if (!this.activeStep) {
      return;
    }

    this.domObserver = new MutationObserver(() => {
      this.scheduleRefreshTarget(100);
      this.autoProgressCompanyStepIfNeeded();
      this.autoProgressMemberStepIfNeeded();
      this.autoProgressTaskStepIfNeeded();
      this.autoProgressProjectStepIfNeeded();
    });
    this.domObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  private autoProgressMemberStepIfNeeded() {
    if (!this.activeStep || this.activeStep.id !== "member") {
      return;
    }

    const memberCreatedToast = this.hasTourSuccessToast([
      "member added",
      "member invited",
      "members added",
    ]);
    if (memberCreatedToast && !this.hasTriggeredMemberAutoProgress && !this.isAutoAdvancing) {
      this.hasTriggeredMemberAutoProgress = true;
      this.isPositionReady = false;
      this.targetRect = null;
      window.setTimeout(async () => {
        if (!this.activeStep || this.activeStep.id !== "member") {
          return;
        }
        try {
          this.isAutoAdvancing = true;
          await this.productTourService.continueTour();
        } finally {
          this.isAutoAdvancing = false;
        }
      }, 150);
      return;
    }
  }

  private autoProgressCompanyStepIfNeeded() {
    if (!this.activeStep || this.activeStep.id !== "company") {
      return;
    }

    const companyCreatedToast = this.hasTourSuccessToast([
      "company added",
      "company created",
      "company create",
      "company create successfully",
    ]);
    if (companyCreatedToast && !this.hasTriggeredCompanyAutoProgress && !this.isAutoAdvancing) {
      this.hasTriggeredCompanyAutoProgress = true;
      this.isPositionReady = false;
      this.targetRect = null;
      window.setTimeout(async () => {
        try {
          this.isAutoAdvancing = true;
          await this.productTourService.continueTour();
        } finally {
          this.isAutoAdvancing = false;
        }
      }, 150);
      return;
    }

    if (this.hasTriggeredCompanyAutoProgress || this.isAutoAdvancing) {
      return;
    }

    const hasCompanyCard = !!document.querySelector("[data-tour='open-company']");
    if (!hasCompanyCard) {
      return;
    }

    this.hasTriggeredCompanyAutoProgress = true;
    window.setTimeout(async () => {
      try {
        this.isAutoAdvancing = true;
        await this.productTourService.continueTour();
      } finally {
        this.isAutoAdvancing = false;
      }
    }, 450);
  }

  private autoProgressTaskStepIfNeeded() {
    if (!this.activeStep || this.activeStep.id !== "task") {
      return;
    }
    if (this.activeStep.isNavigationStep) {
      return;
    }

    const taskCreatedToast = this.hasTourSuccessToast([
      "task created",
      "task added",
    ]);

    if (taskCreatedToast && !this.hasTriggeredTaskAutoProgress && !this.isAutoAdvancing) {
      this.hasTriggeredTaskAutoProgress = true;
      this.isPositionReady = false;
      this.targetRect = null;
      window.setTimeout(async () => {
        if (!this.activeStep || this.activeStep.id !== "task") {
          return;
        }
        try {
          this.isAutoAdvancing = true;
          await this.productTourService.continueTour();
        } finally {
          this.isAutoAdvancing = false;
        }
      }, 150);
      return;
    }

    const addTaskButtonVisible = !!this.getTargetElement(
      "[data-tour='add-task-top'], [data-tour='add-task'], [data-tour='add-task-details']"
    );
    if (
      !addTaskButtonVisible &&
      this.hasSeenTaskDetailsStep &&
      !this.hasTriggeredTaskAutoProgress &&
      !this.isAutoAdvancing
    ) {
      this.hasTriggeredTaskAutoProgress = true;
      window.setTimeout(async () => {
        if (!this.activeStep || this.activeStep.id !== "task") {
          return;
        }
        try {
          this.isAutoAdvancing = true;
          await this.productTourService.continueTour();
        } finally {
          this.isAutoAdvancing = false;
        }
      }, 300);
      return;
    }

    if (
      !this.hasSeenTaskDetailsStep ||
      this.hasTriggeredTaskAutoProgress ||
      this.activeTourTarget !== "add-task" &&
      this.activeTourTarget !== "add-task-top"
    ) {
      return;
    }

    if (this.isAutoAdvancing) {
      return;
    }

    this.hasTriggeredTaskAutoProgress = true;
    window.setTimeout(async () => {
      if (!this.activeStep || this.activeStep.id !== "task") {
        return;
      }

      try {
        this.isAutoAdvancing = true;
        await this.productTourService.continueTour();
      } finally {
        this.isAutoAdvancing = false;
      }
    }, 600);
  }

  private autoProgressProjectStepIfNeeded() {
    if (!this.activeStep || this.activeStep.id !== "project") {
      return;
    }
    if (this.activeStep.isNavigationStep) {
      return;
    }

    const projectCreatedToast = this.hasTourSuccessToast([
      "project created",
      "project added",
    ]);
    if (projectCreatedToast && !this.hasTriggeredProjectAutoProgress && !this.isAutoAdvancing) {
      this.hasTriggeredProjectAutoProgress = true;
      this.isPositionReady = false;
      this.targetRect = null;
      window.setTimeout(async () => {
        if (!this.activeStep || this.activeStep.id !== "project") {
          return;
        }
        try {
          this.isAutoAdvancing = true;
          await this.productTourService.continueTour();
        } finally {
          this.isAutoAdvancing = false;
        }
      }, 150);
      return;
    }

    if (
      !this.hasSeenProjectDetailsStep ||
      this.hasTriggeredProjectAutoProgress ||
      this.activeTourTarget !== "add-project"
    ) {
      return;
    }

    if (this.isAutoAdvancing) {
      return;
    }

    this.hasTriggeredProjectAutoProgress = true;
    window.setTimeout(async () => {
      if (!this.activeStep || this.activeStep.id !== "project") {
        return;
      }

      try {
        this.isAutoAdvancing = true;
        await this.productTourService.continueTour();
      } finally {
        this.isAutoAdvancing = false;
      }
    }, 600);
  }

  private hasTourSuccessToast(keywords: string[]): boolean {
    const nodes = Array.from(
      document.querySelectorAll(".toast-message, .ngx-toastr, .mat-snack-bar-container")
    );

    return nodes.some((node) => {
      const text = (node.textContent || "").toLowerCase();
      return keywords.some((keyword) => text.includes(keyword));
    });
  }

  private isElementMostlyInView(element: HTMLElement): boolean {
    const rect = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    const horizontallyVisible = rect.left >= 0 && rect.right <= viewportWidth;
    const verticallyVisible = rect.top >= 0 && rect.bottom <= viewportHeight;

    return horizontallyVisible && verticallyVisible;
  }

  private setupOutsideClickGuard() {
    this.outsideClickGuardCleanup?.();
    this.outsideClickGuardCleanup = null;

    if (!this.activeStep) {
      return;
    }

    const blockOutsideClick = (event: Event) => {
      const eventTarget = event.target as Element | null;
      if (!eventTarget) {
        return;
      }

      if (eventTarget.closest(".tour-popover")) {
        return;
      }

      if (
        eventTarget.closest(".mat-dialog-container") ||
        eventTarget.closest(".mat-mdc-dialog-container") ||
        eventTarget.closest(".cdk-overlay-pane") ||
        eventTarget.closest(".cdk-overlay-container") ||
        eventTarget.closest(".mat-datepicker-content") ||
        eventTarget.closest(".mat-calendar") ||
        eventTarget.closest(".ng-dropdown-panel") ||
        eventTarget.closest(".mat-select-panel") ||
        eventTarget.closest(".mat-menu-panel") ||
        eventTarget.closest(".owl-dt-container")
      ) {
        return;
      }

      const selector = this.activeStep?.selector;
      if (!selector) {
        event.preventDefault();
        event.stopPropagation();
        (event as any).stopImmediatePropagation?.();
        return;
      }

      const targetElement = this.getTargetElement(selector);
      if (targetElement && targetElement.contains(eventTarget)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      (event as any).stopImmediatePropagation?.();
    };

    document.addEventListener("pointerdown", blockOutsideClick, true);
    document.addEventListener("click", blockOutsideClick, true);
    this.outsideClickGuardCleanup = () => {
      document.removeEventListener("pointerdown", blockOutsideClick, true);
      document.removeEventListener("click", blockOutsideClick, true);
    };
  }
}
